/**
 * LeadSheetViewer — floating band-facing PDF/image overlay for the
 * current song's lead sheet. Renders in an <iframe> for PDFs and an
 * <img> for image formats. Only appears in the presenter console's
 * musician column and in the Stage Display output — audience never sees
 * it.
 */
import React, { useState } from 'react';
import { FileText, X, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeadSheetViewer({ songId, songTitle, url, onClose }) {
  if (!songId || !url) return null;
  const src = `${API}${url}`;
  const isPdf = url.toLowerCase().endsWith('/lead-sheet') || src.toLowerCase().endsWith('.pdf');
  return (
    <div
      data-testid={`lead-sheet-${songId}`}
      className="fixed inset-4 z-50 flex flex-col overflow-hidden rounded-lg border border-slate-700 bg-slate-950 shadow-2xl"
    >
      <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900/90 px-3 py-2">
        <div className="flex items-center gap-2 text-sm text-slate-100">
          <FileText className="h-4 w-4 text-amber-300" />
          <span className="font-semibold">Lead Sheet</span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-300 truncate max-w-[40vw]">{songTitle}</span>
        </div>
        <div className="flex items-center gap-2">
          <a href={src} download target="_blank" rel="noreferrer"
             className="rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] text-slate-200 hover:border-slate-500"
             data-testid="lead-sheet-download">
            <Download className="mr-1 inline-block h-3 w-3" /> Download
          </a>
          <button type="button" data-testid="lead-sheet-close" onClick={onClose}
                  className="rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] text-slate-200 hover:border-slate-500">
            <X className="mr-1 inline-block h-3 w-3" /> Close
          </button>
        </div>
      </div>
      <div className="flex-1 bg-white">
        {/* We render as PDF iframe when we can — falls back to img cleanly if it's a PNG/JPG/WebP (browsers render images in iframes too). */}
        <iframe title="Lead Sheet" src={src} className="h-full w-full border-0" />
      </div>
    </div>
  );
}


/**
 * LeadSheetUpload — small `<input type=file>` widget used inside the
 * song editor. Uploads to `POST /admin/worship/songs/{id}/lead-sheet`.
 */
export function LeadSheetUpload({ songId, currentUrl, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { getToken } = await import('@/lib/authToken');
      const axios = (await import('axios')).default;
      const r = await axios.post(
        `${API}/api/admin/worship/songs/${songId}/lead-sheet`,
        form,
        { headers: { Authorization: `Bearer ${getToken()}` }, timeout: 60000 },
      );
      onChange?.(r.data?.url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    setUploading(true);
    setError('');
    try {
      const { getToken } = await import('@/lib/authToken');
      const axios = (await import('axios')).default;
      await axios.delete(
        `${API}/api/admin/worship/songs/${songId}/lead-sheet`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      onChange?.(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded border border-slate-800 bg-slate-950/60 p-2" data-testid={`lead-sheet-upload-${songId}`}>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Lead Sheet (PDF/PNG/JPG)</div>
      {currentUrl && (
        <div className="mb-1 flex items-center justify-between text-xs">
          <a href={`${API}${currentUrl}`} target="_blank" rel="noreferrer" className="text-amber-300 hover:underline">
            <FileText className="mr-1 inline-block h-3 w-3" /> View current
          </a>
          <button type="button" data-testid={`lead-sheet-remove-${songId}`} onClick={remove}
                  className="text-rose-400 text-[10px] hover:underline">Remove</button>
        </div>
      )}
      <input
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        data-testid={`lead-sheet-file-${songId}`}
        onChange={(e) => upload(e.target.files?.[0])}
        disabled={uploading}
        className="text-[10px] text-slate-400"
      />
      {uploading && <p className="text-[9px] text-slate-400 mt-1">Uploading…</p>}
      {error && <p className="text-[10px] text-rose-400 mt-1">{error}</p>}
    </div>
  );
}
